import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { gql } from 'graphql-request'
import { Flame, Headphones, Star, Trophy, Target, ChevronDown, ChevronRight } from 'lucide-react'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { useStores } from '@/components/StoreProvider'
import { deriveCardStatus, deriveMasteryPct, displayRolePlayTitle, M2CardStatus, M2StatusPill, M2CardFoot } from './rolePlayStatus'

// --- GraphQL (reused queries; no new backend) ---
const GET_USER_MODULES = gql`
  query getUserModules {
    getUserModules {
      id
      name
    }
  }
`

const GET_ORGANISATION_TARGETS = gql`
  query getOrganisationTargets($organisationId: Int!) {
    getOrganisationTargets(organisationId: $organisationId) {
      id
      moduleId
      payloadId
      payloadName
      minRating
      minSessions
    }
  }
`

const GET_ROLE_PLAY_STATS = gql`
  query GetRolePlayStats {
    getRolePlayStats {
      rolePlayId
      sessionsPlayed
      totalScore
    }
  }
`

const GET_REVIEWS = gql`
  query GetReviews($limit: Int, $offset: Int) {
    getReviews(limit: $limit, offset: $offset) {
      rolePlayId
      score
      mastered
      toImprove
      createdAt
    }
  }
`

interface ModuleRow {
  id: number
  name: string
}
interface TargetRow {
  id: number
  moduleId: number
  payloadId: number
  payloadName: string
  minRating: number
  minSessions: number
}
interface StatRow {
  rolePlayId: number
  sessionsPlayed: number
  totalScore: number
}
interface ReviewRow {
  rolePlayId: number
  score: number
  mastered: string | null
  toImprove: string | null
  createdAt: string
}

interface RolePlayVM {
  id: number
  title: string
  status: M2CardStatus
  sessionsPlayed: number
  targetSessions: number
  totalScore: number
  targetScore: number
  avgStars: number
  masteryPct: number
}
interface ModuleVM {
  id: number
  name: string
  roleplays: RolePlayVM[]
  masteredCount: number
  total: number
  avgMasteryPct: number
}

type TimeRange = 'week' | 'month' | 'quarter'

const RANGE_LABEL: Record<TimeRange, string> = { week: 'This week', month: 'This month', quarter: 'This quarter' }
const RANGE_NOUN: Record<TimeRange, string> = { week: 'this week', month: 'this month', quarter: 'this quarter' }
const RANGE_PREV: Record<TimeRange, string> = { week: 'last week', month: 'last month', quarter: 'last quarter' }

// Start of the current window, and start of the previous equal-length window, for the given range.
const getRangeBounds = (range: TimeRange): { start: Date; prevStart: Date } => {
  const now = new Date()
  if (range === 'week') {
    const start = new Date(now)
    start.setHours(0, 0, 0, 0)
    const diffToMonday = (now.getDay() + 6) % 7 // 0=Mon ... 6=Sun
    start.setDate(now.getDate() - diffToMonday)
    const prevStart = new Date(start)
    prevStart.setDate(start.getDate() - 7)
    return { start, prevStart }
  }
  if (range === 'month') {
    return { start: new Date(now.getFullYear(), now.getMonth(), 1), prevStart: new Date(now.getFullYear(), now.getMonth() - 1, 1) }
  }
  const q = Math.floor(now.getMonth() / 3)
  return { start: new Date(now.getFullYear(), q * 3, 1), prevStart: new Date(now.getFullYear(), q * 3 - 3, 1) }
}

// Consecutive days (ending today, with a one-day grace) that have at least one review.
const computeStreak = (reviews: ReviewRow[]): number => {
  if (reviews.length === 0) return 0
  const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  const days = new Set(reviews.map((r) => dayKey(new Date(r.createdAt))))
  const cursor = new Date()
  cursor.setHours(0, 0, 0, 0)
  if (!days.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1) // grace: streak survives until end of today
  let streak = 0
  while (days.has(dayKey(cursor))) {
    streak++
    cursor.setDate(cursor.getDate() - 1)
  }
  return streak
}

const initials = (firstName?: string, lastName?: string): string => {
  const a = firstName?.trim()?.[0] ?? ''
  const b = lastName?.trim()?.[0] ?? ''
  return (a + b).toUpperCase() || 'U'
}

// Dashboard-local role play card, built from the same shared pieces as the coach screen's cards column
// (status pill, footer) so the two always look alike without sharing selection logic.
const RolePlayCard: React.FC<{ rp: RolePlayVM }> = ({ rp }) => (
  <div className="relative rounded-[11px] px-3.5 py-3 flex flex-col bg-white border" style={{ borderColor: 'var(--m2-line)' }}>
    <div className="flex items-center justify-end gap-2 mb-2">
      <M2StatusPill status={rp.status} />
    </div>
    <h3 className="font-bold text-[13.5px] leading-[1.3] tracking-[-0.2px] mb-2" style={{ color: 'var(--m2-ink)' }}>
      {displayRolePlayTitle(rp.title)}
    </h3>
    <M2CardFoot sessionsPlayed={rp.sessionsPlayed} targetSessions={rp.targetSessions} avgStars={rp.avgStars} />
  </div>
)

const ModuleCard: React.FC<{ module: ModuleVM }> = ({ module }) => {
  const [open, setOpen] = useState(true)
  return (
    <div className="rounded-[14px] border overflow-hidden" style={{ background: 'var(--m2-card)', borderColor: open ? 'var(--m2-mint-deep)' : 'var(--m2-line)' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-4 p-4 text-left">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex items-center justify-center w-[38px] h-[38px] rounded-[10px] shrink-0" style={{ background: open ? 'var(--m2-lime)' : 'var(--m2-mint)', color: 'var(--m2-brand)' }}>
            <Trophy size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-[16px] font-bold leading-tight truncate" style={{ color: 'var(--m2-ink)' }}>
              {module.name}
            </div>
            <div className="text-[11.5px] mt-0.5" style={{ color: 'var(--m2-muted)' }}>
              {module.total} {module.total === 1 ? 'role-play' : 'role-plays'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3.5 shrink-0">
          <div className="text-right leading-tight">
            <div className="text-[15px] font-bold" style={{ color: 'var(--m2-ink)' }}>
              {module.avgMasteryPct}%
            </div>
            <div className="text-[10.5px] font-medium mt-0.5" style={{ color: 'var(--m2-muted)' }}>
              {module.masteredCount} of {module.total} mastered
            </div>
          </div>
          <div className="hidden sm:block w-[100px] h-[6px] rounded-[3px] overflow-hidden" style={{ background: 'var(--m2-line)' }}>
            <div className="h-full rounded-[3px]" style={{ width: `${module.avgMasteryPct}%`, background: 'linear-gradient(90deg, #A4D447 0%, #C5F26A 100%)' }} />
          </div>
          {open ? <ChevronDown size={16} style={{ color: 'var(--m2-muted)' }} /> : <ChevronRight size={16} style={{ color: 'var(--m2-muted)' }} />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t" style={{ borderColor: 'var(--m2-line-soft)' }}>
          {module.roleplays.length === 0 ? (
            <div className="py-6 text-center text-[12px]" style={{ color: 'var(--m2-muted)' }}>
              No role plays configured for this module yet.
            </div>
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 mt-3">
              {module.roleplays.map((rp) => (
                <RolePlayCard key={rp.id} rp={rp} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const KpiCard: React.FC<{ children: React.ReactNode; dark?: boolean }> = ({ children, dark }) => (
  <div className="rounded-[10px] border p-3.5" style={dark ? { background: 'var(--m2-brand)', borderColor: 'var(--m2-brand)' } : { background: 'var(--m2-card)', borderColor: 'var(--m2-line)' }}>
    {children}
  </div>
)

const Module2Dashboard: React.FC = () => {
  const { userStore } = useStores()
  const user = userStore.user
  const [range, setRange] = useState<TimeRange>('week')

  const [modules, setModules] = useState<ModuleRow[]>([])
  const [targets, setTargets] = useState<TargetRow[]>([])
  const [stats, setStats] = useState<StatRow[]>([])
  const [reviews, setReviews] = useState<ReviewRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const orgId = user?.organization_id
        if (!orgId) throw new Error('Missing organisation')
        const [modulesRes, targetsRes, statsRes, reviewsRes] = await Promise.all([
          authorizedGraphQLRequest<{ getUserModules: ModuleRow[] }>(GET_USER_MODULES),
          authorizedGraphQLRequest<{ getOrganisationTargets: TargetRow[] }>(GET_ORGANISATION_TARGETS, { organisationId: orgId }),
          authorizedGraphQLRequest<{ getRolePlayStats: StatRow[] }>(GET_ROLE_PLAY_STATS),
          authorizedGraphQLRequest<{ getReviews: ReviewRow[] }>(GET_REVIEWS, { limit: 0, offset: 0 }),
        ])
        if (cancelled) return
        setModules(modulesRes.getUserModules || [])
        setTargets(targetsRes.getOrganisationTargets || [])
        setStats(statsRes.getRolePlayStats || [])
        setReviews(reviewsRes.getReviews || [])
      } catch (err) {
        console.error('Failed to load dashboard:', err)
        if (!cancelled) setError('Failed to load dashboard data')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [user?.organization_id])

  // Build per-module role play view-models (mirrors the card build in Module2Chat).
  const moduleVMs = useMemo<ModuleVM[]>(() => {
    const statsMap = new Map(stats.map((s) => [s.rolePlayId, s]))
    return modules.map((mod) => {
      const roleplays: RolePlayVM[] = targets
        .filter((t) => t.moduleId === mod.id)
        .map((t) => {
          const stat = statsMap.get(t.payloadId)
          const card = {
            sessionsPlayed: stat?.sessionsPlayed || 0,
            targetSessions: t.minSessions || 0,
            totalScore: stat?.totalScore || 0,
            targetScore: t.minRating || 0,
          }
          return {
            id: t.payloadId,
            title: t.payloadName,
            status: deriveCardStatus(card),
            sessionsPlayed: card.sessionsPlayed,
            targetSessions: card.targetSessions,
            totalScore: card.totalScore,
            targetScore: card.targetScore,
            avgStars: card.sessionsPlayed > 0 ? card.totalScore / card.sessionsPlayed : 0,
            masteryPct: deriveMasteryPct(card),
          }
        })
      const masteredCount = roleplays.filter((r) => r.status === 'mastered').length
      const avgMasteryPct = roleplays.length > 0 ? Math.round(roleplays.reduce((sum, r) => sum + r.masteryPct, 0) / roleplays.length) : 0
      return { id: mod.id, name: mod.name, roleplays, masteredCount, total: roleplays.length, avgMasteryPct }
    })
  }, [modules, targets, stats])

  const allRoleplays = useMemo(() => moduleVMs.flatMap((m) => m.roleplays), [moduleVMs])

  // Overview KPIs derived from reviews (each review === a session) + the role play view-models.
  const kpis = useMemo(() => {
    const { start, prevStart } = getRangeBounds(range)
    const inCurrent = reviews.filter((r) => new Date(r.createdAt) >= start)
    const inPrev = reviews.filter((r) => {
      const d = new Date(r.createdAt)
      return d >= prevStart && d < start
    })
    const avg = (rows: ReviewRow[]) => (rows.length > 0 ? rows.reduce((s, r) => s + r.score, 0) / rows.length : null)
    const currentAvg = avg(inCurrent)
    const prevAvg = avg(inPrev)

    const mastered = allRoleplays.filter((r) => r.status === 'mastered')
    // Next milestone = the non-mastered role play closest to mastery.
    const nextMilestone = allRoleplays.filter((r) => r.status !== 'mastered').sort((a, b) => b.masteryPct - a.masteryPct)[0]
    const sessionsToGo = nextMilestone && nextMilestone.targetSessions > 0 ? Math.max(nextMilestone.targetSessions - nextMilestone.sessionsPlayed, 1) : null

    return {
      sessions: inCurrent.length,
      sessionsDelta: inCurrent.length - inPrev.length,
      avgScore: currentAvg,
      avgScoreDelta: currentAvg != null && prevAvg != null ? currentAvg - prevAvg : null,
      masteredCount: mastered.length,
      totalRoleplays: allRoleplays.length,
      hasRoleplays: allRoleplays.length > 0,
      topSkills: mastered.slice(0, 3).map((r) => r.title),
      nextMilestone: nextMilestone ? { title: nextMilestone.title, sessionsToGo } : null,
    }
  }, [reviews, range, allRoleplays])

  const streak = useMemo(() => computeStreak(reviews), [reviews])

  if (isLoading) {
    return (
      <div className="module2-coach flex flex-1 items-center justify-center" style={{ background: 'var(--m2-bg)' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: 'var(--m2-mint-deep)', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: 'var(--m2-muted)' }}>
            Loading your progress…
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="module2-coach flex flex-1 items-center justify-center" style={{ background: 'var(--m2-bg)' }}>
        <p className="text-sm" style={{ color: 'var(--m2-rose-ink)' }}>
          {error}
        </p>
      </div>
    )
  }

  return (
    <div className="module2-coach flex flex-1 flex-col min-h-0 overflow-y-auto" style={{ background: 'var(--m2-bg)' }}>
      <div className="w-full max-w-[980px] mx-auto p-4 md:p-6 flex flex-col gap-4">
        {/* Page header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-[26px] md:text-[28px] font-bold tracking-tight" style={{ color: 'var(--m2-ink)' }}>
              Your progress
            </h1>
            <p className="text-[14px] mt-1" style={{ color: 'var(--m2-muted)' }}>
              See how you're doing
            </p>
          </div>
          <div className="flex items-center gap-3.5">
            {user && (
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold" style={{ background: 'var(--m2-brand)', color: 'var(--m2-lime)' }}>
                  {initials(user.firstName, user.lastName)}
                </span>
                <span className="text-[14px] font-semibold" style={{ color: 'var(--m2-ink)' }}>
                  {user.firstName} {user.lastName}
                </span>
              </div>
            )}
            {streak > 0 && (
              <div className="flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ background: 'var(--m2-mint)' }}>
                <Flame size={14} style={{ color: 'var(--m2-brand)' }} />
                <span className="text-[12px] font-semibold" style={{ color: 'var(--m2-brand)' }}>
                  {streak}-day streak
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Overview */}
        <div>
          <div className="flex items-center justify-between mb-3.5">
            <span className="text-[10px] font-semibold tracking-[1.8px]" style={{ color: 'var(--m2-muted)' }}>
              OVERVIEW
            </span>
            <div className="inline-flex rounded-full p-[3px] gap-0.5 border" style={{ background: 'var(--m2-card)', borderColor: 'var(--m2-line)' }}>
              {(['week', 'month', 'quarter'] as TimeRange[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  className="px-3.5 py-1.5 text-[11.5px] font-semibold rounded-full transition-colors"
                  style={range === r ? { background: 'var(--m2-brand)', color: '#fff' } : { background: 'transparent', color: 'var(--m2-muted)' }}
                >
                  {RANGE_LABEL[r]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {/* Sessions */}
            <KpiCard>
              <div className="flex items-center gap-1.5 text-[10.5px] mb-1.5" style={{ color: 'var(--m2-muted)' }}>
                <Headphones size={13} />
                Sessions {RANGE_NOUN[range]}
              </div>
              <div className="text-[22px] font-bold leading-none" style={{ color: 'var(--m2-ink)' }}>
                {kpis.sessions}
              </div>
              {kpis.sessionsDelta !== 0 && (
                <div className="text-[10px] mt-1.5 font-medium" style={{ color: kpis.sessionsDelta > 0 ? '#3D8B5C' : 'var(--m2-muted)' }}>
                  {kpis.sessionsDelta > 0 ? '↑' : '↓'} {Math.abs(kpis.sessionsDelta)} vs {RANGE_PREV[range]}
                </div>
              )}
            </KpiCard>

            {/* Average score */}
            <KpiCard>
              <div className="flex items-center gap-1.5 text-[10.5px] mb-1.5" style={{ color: 'var(--m2-muted)' }}>
                <Star size={13} />
                Average score
              </div>
              <div className="flex items-baseline gap-1">
                <div className="text-[22px] font-bold leading-none" style={{ color: 'var(--m2-ink)' }}>
                  {kpis.avgScore != null ? kpis.avgScore.toFixed(1) : '—'}
                </div>
                <div className="text-[11px]" style={{ color: 'var(--m2-muted)' }}>
                  / 5.0
                </div>
              </div>
              {kpis.avgScoreDelta != null && Math.abs(kpis.avgScoreDelta) >= 0.05 && (
                <div className="text-[10px] mt-1.5 font-medium" style={{ color: kpis.avgScoreDelta > 0 ? '#3D8B5C' : 'var(--m2-muted)' }}>
                  {kpis.avgScoreDelta > 0 ? '↑' : '↓'} {Math.abs(kpis.avgScoreDelta).toFixed(1)} vs {RANGE_PREV[range]}
                </div>
              )}
            </KpiCard>

            {/* Skills mastered (dark) */}
            <KpiCard dark>
              <div className="flex items-start justify-between gap-2 mb-2.5">
                <div className="flex items-center gap-1.5 text-[10.5px]" style={{ color: 'var(--m2-lime)' }}>
                  <Trophy size={13} />
                  Skills mastered
                </div>
                <div className="text-[18px] font-bold leading-none shrink-0" style={{ color: 'var(--m2-lime)' }}>
                  {kpis.masteredCount}
                  <span className="text-[11px] font-medium ml-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    of {kpis.totalRoleplays}
                  </span>
                </div>
              </div>
              {kpis.topSkills.length > 0 ? (
                <ul className="flex flex-col gap-1.5">
                  {kpis.topSkills.map((skill) => (
                    <li key={skill} className="flex items-start gap-1.5 text-[11.5px] leading-snug" style={{ color: 'rgba(255,255,255,0.92)' }}>
                      <span className="w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 mt-px" style={{ background: 'var(--m2-lime)', color: 'var(--m2-brand)' }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12l5 5l10 -10" />
                        </svg>
                      </span>
                      <span className="truncate">{skill}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11.5px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  No skills mastered yet — keep practicing!
                </p>
              )}
            </KpiCard>

            {/* Next milestone */}
            <KpiCard>
              <div className="flex items-center gap-1.5 text-[10.5px] mb-1.5" style={{ color: 'var(--m2-muted)' }}>
                <Target size={13} />
                Next milestone
              </div>
              {kpis.nextMilestone ? (
                <>
                  <div className="text-[13px] font-semibold leading-snug" style={{ color: 'var(--m2-ink)' }}>
                    {displayRolePlayTitle(kpis.nextMilestone.title)}
                  </div>
                  {kpis.nextMilestone.sessionsToGo != null && (
                    <div className="text-[10px] mt-1" style={{ color: 'var(--m2-muted)' }}>
                      {kpis.nextMilestone.sessionsToGo} {kpis.nextMilestone.sessionsToGo === 1 ? 'session' : 'sessions'} to go
                    </div>
                  )}
                </>
              ) : kpis.hasRoleplays ? (
                <div className="text-[13px] font-semibold" style={{ color: 'var(--m2-ink)' }}>
                  All mastered 🎉
                </div>
              ) : (
                <div className="text-[13px] font-semibold" style={{ color: 'var(--m2-muted)' }}>
                  —
                </div>
              )}
            </KpiCard>
          </div>
        </div>

        {/* Your modules */}
        <div className="flex flex-col gap-3.5">
          <span className="text-[10px] font-semibold tracking-[1.8px]" style={{ color: 'var(--m2-muted)' }}>
            YOUR MODULES
          </span>
          {moduleVMs.length === 0 ? (
            <div className="rounded-[14px] border p-8 text-center text-[13px]" style={{ background: 'var(--m2-card)', borderColor: 'var(--m2-line)', color: 'var(--m2-muted)' }}>
              No modules assigned yet.
            </div>
          ) : (
            moduleVMs.map((mod) => <ModuleCard key={mod.id} module={mod} />)
          )}
        </div>
      </div>
    </div>
  )
}

export default observer(Module2Dashboard)
